import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getRefrigerantList,
  addRefrigerantToBusiness,
  updateRefrigerant,
  deleteRefrigerant,
} from "../../../../../../apis/Business/RefrigerantApi";
import { getGasRefrigerant } from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const RefrigerationTable = ({ businessId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownGasRefrigerant, setDropdownGasRefrigerant] = useState([]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getRefrigerantList({
      businessId,
      setObjects: setData,
    });
    getGasRefrigerant({ setDropdown: setDropdownGasRefrigerant });
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = useCallback(
    (editedItem) => {
      updateRefrigerant({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        aircon: editedItem["aircon.value"]
          ? editedItem["aircon.value"].value
          : editedItem["aircon"]
          ? editedItem["aircon"].value
          : null,
        gas_used: null,
        capacity: editedItem.capacity,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [businessId, data, setData]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addRefrigerantToBusiness({
        businessId,
        ...newItem,
        aircon: newItem["aircon.value"] ? newItem["aircon.value"].value : null,
        capacity: newItem.capacity,
        gas_used: null,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteRefrigerant({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "invoice_date",
        header: t("INVOICE_DATE"),
        type: "text",
        canHide: true,
        canSort: true,
        placeholder: t("DD_MM_YYYY"),
        optional: true,
        width: "80px",
      },
      {
        accessor: "invoice_number",
        header: t("INVOICE_NO"),
        type: "text",
        canSort: true,
        canHide: true,
        optional: true,
        width: "80px",
      },
      {
        accessor: "aircon.value",
        header: `${t("REFRIGERATION_GAS")}*`,
        type: "dropdown",
        dropdownData: dropdownGasRefrigerant,
        disableSortBy: true,
        width: "130px",
      },
      {
        accessor: "nickname",
        header: t("NICKNAME"),
        type: "text",
        disableSortBy: true,
        placeholder: t("NAME"),
        width: "100px",
      },
      {
        accessor: "pcs",
        header: `${t("NO_PCS")}*`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
      },
      {
        accessor: "capacity",
        header: `${t("REFRIGERATION_CAPACITY")}*`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "90px",
        capacityPlaceholder: true,
      },
      {
        accessor: "percentage_turned_on",
        header: `${t("REFRIGERATION_TURNED_ON")}*`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "90px",
        tipHeader: "Tip",
        tipBody: t("TIPBOX_IN_USED"),
        minValue: 1,
        maxValue: 100,
      },
      {
        accessor: "disposed",
        header: t("REFRIGERATION_DISPOSED"),
        type: "switch",
        disableSortBy: true,
        width: "100px",
      },
      {
        accessor: "leased",
        header: t("LEASED?"),
        type: "switch",
        disableSortBy: true,
        width: "100px",
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "price_invoice",
        header: t("PRICE_INVOICE"),
        type: "number",
        canSort: true,
        canHide: true,
        placeholder: "0",
        optional: true,
        subtotalUnit: "DKK",
        width: "90px",
        tipHeader: "Tip",
        tipBody: t("PRICE_INVOICE_TIP"),
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "90px",
      },
    ],
    [dropdownGasRefrigerant, t]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    price_invoice: { label: t("TOTAL_PRICE") },
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "aircon.value": dropdownGasRefrigerant,
    }),
    [dropdownGasRefrigerant]
  );

  const dynamicPlaceholders = useMemo(
    () => ({
      "aircon.value": {
        targetField: "capacity",
        getPlaceholder: (selectedValue, dropdownData) => {
          const selectedItem = dropdownData.find(
            (item) =>
              item.dropdown_name === selectedValue ||
              item.dropdown_name_en === selectedValue
          );
          return selectedItem?.capacity_placeholder;
        },
        dropdownDataKey: "aircon.value",
      },
    }),
    []
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default RefrigerationTable;
